import type { QueryClient } from '@tanstack/react-query';

import {
  CurrentCompanyJobSlotsSubscription,
  Job,
  JobV2,
  WorkAddress,
} from '../../interfaces/entities';

export function normalizeJobV2Response(jobV2: JobV2): Job {
  const companyAddressInLink = jobV2.links?.companyAddress as
    | WorkAddress
    | undefined;

  return {
    ...jobV2,
    country: jobV2.Country
      ? { ...jobV2.Country, isWhitelisted: jobV2.Country.isWhitelisted ?? true }
      : undefined,
    // The V2 read endpoint currently returns this relation as
    // `unfilteredSkills`, while mutation responses use `skills`.
    // Keep both response shapes usable by consumers such as the screening
    // question answer table.
    skills:
      jobV2.skills?.map(skill => ({ skill })) ?? jobV2.unfilteredSkills ?? [],
    salaries: jobV2.JobSalaries ?? jobV2.links?.jobSalaries,
    companyAddress:
      jobV2.companyAddress ??
      (companyAddressInLink
        ? {
            ...companyAddressInLink,
            poi:
              companyAddressInLink?.poi ??
              companyAddressInLink?.pointOfInterest,
          }
        : undefined),
    descriptionJsonString: jobV2.descriptionRaw
      ? JSON.stringify(jobV2.descriptionRaw)
      : jobV2.descriptionJsonString,
    interviewProcessJsonString: jobV2.interviewProcessRaw
      ? JSON.stringify(jobV2.interviewProcessRaw)
      : jobV2.interviewProcessJsonString,
    location: jobV2.hierarchicalLocation ?? jobV2.location,
    hierarchicalJobCategory: jobV2.hierarchicalJobCategory && {
      ...jobV2.hierarchicalJobCategory,
      leaves: [jobV2.hierarchicalJobCategory],
    },
    screeningQuestions:
      jobV2.screeningQuestions ?? jobV2.links?.screeningQuestion,
    matchQuestions: jobV2.keywordMatchCriteria?.questions,
    matchAnswers: jobV2.keywordMatchCriteria?.answers,
  } as Job;
}

export type JobV2WriteResponseEnvelope = {
  links?: {
    currentCompanyJobSlotsSubscription?: CurrentCompanyJobSlotsSubscription;
  };
  data: Job;
};

export const pickCurrentCompanyJobSlotsSubscription = (
  body: JobV2WriteResponseEnvelope
): CurrentCompanyJobSlotsSubscription | undefined =>
  body.data?.links?.currentCompanyJobSlotsSubscription ??
  body.links?.currentCompanyJobSlotsSubscription;

type JobV2CacheUpdate = Pick<Job, 'status' | 'updatedAt'> &
  Partial<
    Pick<
      Job,
      | 'closedAt'
      | 'expiryDate'
      | 'jobExtension'
      | 'jobExtensionDetails'
      | 'closureInfo'
      | 'closureFeedbackEligible'
      | 'showClosureFeedbackAutomatically'
    >
  >;

export function patchCachedJobFromV2(
  queryClient: QueryClient,
  jobId: string,
  updatedJob: JobV2CacheUpdate
) {
  queryClient.setQueryData<Job>(['job-by-id', jobId], currentJob => {
    if (!currentJob) return currentJob;

    const nextJob: Job = {
      ...currentJob,
      status: updatedJob.status,
      updatedAt: updatedJob.updatedAt,
      jobExtensionDetails:
        updatedJob.jobExtensionDetails ??
        updatedJob.jobExtension ??
        currentJob.jobExtensionDetails,
    };

    if (updatedJob.expiryDate !== undefined) {
      nextJob.expiryDate = updatedJob.expiryDate;
    }
    if (updatedJob.closedAt !== undefined) {
      nextJob.closedAt = updatedJob.closedAt;
    }

    // A new status transition invalidates the previous closure cycle immediately.
    // Write responses may omit summaries; the following refetch restores them.
    const statusChanged = currentJob.status !== updatedJob.status;
    if (updatedJob.closureInfo !== undefined || statusChanged) {
      nextJob.closureInfo = updatedJob.closureInfo ?? null;
    }
    if (updatedJob.closureFeedbackEligible !== undefined || statusChanged) {
      nextJob.closureFeedbackEligible =
        updatedJob.closureFeedbackEligible ?? false;
    }
    if (
      updatedJob.showClosureFeedbackAutomatically !== undefined ||
      statusChanged
    ) {
      nextJob.showClosureFeedbackAutomatically =
        updatedJob.showClosureFeedbackAutomatically ?? false;
    }

    return nextJob;
  });

  return queryClient.invalidateQueries({ queryKey: ['job-by-id', jobId] });
}
